import { Box, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { ReviewOption } from './ReviewOption';
import { VariableMatchViewDetailsButton } from './VariableMatchViewDetailsButton';
import type { AvSelectionCheckpoint } from '../types/epi-chat-types';
import type { VariableMatchSelection } from '../types/variable-match-types';

interface VariableMatchStepProps {
  checkpoint: AvSelectionCheckpoint;
  selection: VariableMatchSelection | null;
  buildNewLabel: string;
  disabled: boolean;
  onSelect: (selection: VariableMatchSelection) => void;
}

/** One variable's question: its exact match (if any) and "Build a new variable". */
export const VariableMatchStep = ({
  checkpoint,
  selection,
  buildNewLabel,
  disabled,
  onSelect,
}: VariableMatchStepProps) => {
  const requested = checkpoint.requestedVariable ?? checkpoint.summary;
  const groupName = `variable-match-${checkpoint.checkpointId}`;
  // Only the exact match (`primary`) is offered; similar alternatives are not.
  const candidate = checkpoint.primary;

  return (
    <>
      <Typography variant="body2">
        <FormattedMessage
          id="epiChat.variableMatch.matchFor"
          defaultMessage="Match for <strong>{name}</strong>"
          values={{
            name: requested,
            strong: (chunks) => (
              <Box component="strong" sx={{ fontWeight: 600 }}>
                {chunks}
              </Box>
            ),
          }}
        />
      </Typography>

      <Stack spacing={2} role="radiogroup" aria-label={requested}>
        {candidate && (
          <ReviewOption
            name={groupName}
            label={candidate.dataElement}
            selected={
              selection?.type === 'use' &&
              selection.candidate.analyticalVariableId === candidate.analyticalVariableId
            }
            disabled={disabled}
            onSelect={() => onSelect({ type: 'use', candidate })}
            data-testid={`variable-match-option-${candidate.analyticalVariableId}`}
          >
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }} color="text.secondary">
              {candidate.name}
            </Typography>
            {candidate.reason && (
              <Typography variant="body2" color="text.secondary">
                {candidate.reason}
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <VariableMatchViewDetailsButton candidate={candidate} />
            </Box>
          </ReviewOption>
        )}

        {checkpoint.allowBuildNew && (
          <ReviewOption
            name={groupName}
            label={buildNewLabel}
            selected={selection?.type === 'build'}
            disabled={disabled}
            onSelect={() => onSelect({ type: 'build' })}
            data-testid="variable-match-build-new"
          />
        )}
      </Stack>
    </>
  );
};
